<template>
	<div>
		<DashboardHeader
			v-if="order"
			:title="purchaserName"
			:subtitle="`Order ${order.orderId}`"
			:buttonItems="buttonItems"
			@click="handleHeaderClick"
		/>
		<div v-if="order" class="container-fluid mt--6">
			<b-overlay :show="isLoading" rounded="sm" no-wrap />

			<!-- Top row: purchaser + status -->
			<div class="row">
				<div class="col-md-8">
					<Card body-classes="p-3">
						<h5 class="text-uppercase text-muted mb-2">Purchaser</h5>
						<h2 class="title m-0">{{ purchaserName }}</h2>
						<h6 class="m-0 text-muted">
							<a :href="`mailto:${order.buyer.email}`">{{ order.buyer.email }}</a>
						</h6>
						<h6 v-if="order.buyer.businessName" class="m-0 text-muted">
							{{ order.buyer.businessName }}
						</h6>
					</Card>
				</div>
				<div class="col-md-4">
					<Card body-classes="p-3">
						<h5 class="text-uppercase text-muted mb-2">Status</h5>
						<h3 class="m-0">
							<b-badge :variant="statusVariant(order.status)">{{ order.status }}</b-badge>
							<small v-if="order.isTestMode" class="text-warning ml-2">TEST MODE</small>
						</h3>
						<small class="text-muted d-block mt-2">
							{{ formatTimestamp(order.createdAt) }}
						</small>
					</Card>
				</div>
			</div>

			<!-- Exhibitor business created by this order. Attendee orders never
			     enter this branch, preserving the existing detail layout. -->
			<div v-if="isExhibitor && order.business" class="row">
				<div class="col">
					<Card body-classes="p-3">
						<div class="d-flex align-items-center">
							<img
								v-if="order.business.businessLogo"
								:src="order.business.businessLogo"
								alt="Business logo"
								class="business-logo mr-3"
							/>
							<div class="flex-grow-1">
								<h5 class="text-uppercase text-muted mb-1">Exhibitor Business</h5>
								<h2 class="title m-0">{{ order.business.businessName }}</h2>
								<p class="text-muted mb-1">{{ order.business.businessBio }}</p>
								<b-badge variant="primary" class="text-uppercase">
									{{ order.mainProductType || order.business.businessLevel || 'other' }}
								</b-badge>
								<router-link
									v-if="order.businessId"
									class="ml-3 small"
									:to="{
										name: vendorViewRouteName,
										params: { businessId: order.businessId }
									}"
								>
									View exhibitor profile
								</router-link>
							</div>
						</div>
					</Card>
				</div>
			</div>

			<!-- Items + totals -->
			<div class="row">
				<div class="col">
					<Card no-body>
						<b-row align-v="center" slot="header" class="px-2">
							<b-col><h3 class="mb-0">Items Purchased</h3></b-col>
							<b-col cols="auto">
								<small class="text-muted">
									{{ order.items ? order.items.length : 0 }}
									{{ order.items && order.items.length === 1 ? 'item' : 'items' }}
								</small>
							</b-col>
						</b-row>
						<b-table
							:items="order.items || []"
							:fields="itemFields"
							hover
							responsive
							class="mb-0"
						>
							<template #cell(productTitle)="row">
								<strong>{{ row.item.productTitle }}</strong>
								<br />
								<small class="text-muted text-capitalize">
									{{ row.item.exhibitorProductType || row.item.productType || 'pass' }}
									<span v-if="row.item.accountLevel">
										&middot; {{ row.item.accountLevel }}
									</span>
								</small>
							</template>
							<template #cell(quantity)="row">
								{{ row.item.quantity }}
							</template>
							<template #cell(unitAmount)="row">
								{{ amountToDollars(row.item.unitAmount) }}
							</template>
							<template #cell(lineTotal)="row">
								<strong>{{ amountToDollars(row.item.lineTotal) }}</strong>
							</template>
						</b-table>
						<div class="border-top totals-block">
							<b-row class="totals-line">
								<b-col><small class="text-muted">Subtotal</small></b-col>
								<b-col class="text-right">
									<small class="text-muted">
										{{ amountToDollars(order.subtotalCents) }}
									</small>
								</b-col>
							</b-row>
							<b-row v-if="order.couponDiscountCents > 0" class="totals-line">
								<b-col>
									<small class="text-muted">Coupon ({{ order.couponCode }})</small>
								</b-col>
								<b-col class="text-right">
									<small class="text-success">
										-{{ amountToDollars(order.couponDiscountCents) }}
									</small>
								</b-col>
							</b-row>
							<b-row class="totals-line">
								<b-col><small class="text-muted">Transaction Fee</small></b-col>
								<b-col class="text-right">
									<small class="text-muted">
										{{ amountToDollars(order.feeCents) }}
									</small>
								</b-col>
							</b-row>
							<b-row class="totals-line totals-grand mt-3 pt-2 border-top">
								<b-col>
									<span class="totals-grand-label">Total Charged</span>
								</b-col>
								<b-col class="text-right">
									<span class="totals-grand-value">
										{{ amountToDollars(order.totalCents) }}
									</span>
								</b-col>
							</b-row>
						</div>
					</Card>
				</div>
			</div>

			<!-- Attendees / exhibitor contacts -->
			<div class="row">
				<div class="col">
					<Card no-body>
						<b-row align-v="center" slot="header" class="px-2">
							<b-col><h3 class="mb-0">{{ isExhibitor ? 'Vendor Contacts' : 'Attendees' }}</h3></b-col>
							<b-col cols="auto">
								<small class="text-muted">
									{{ people.length }}
								</small>
							</b-col>
						</b-row>
						<b-table
							:items="attendeeRows"
							:fields="attendeeFields"
							hover
							responsive
							class="mb-0"
						>
							<template #cell(name)="row">
								<strong>{{ row.item.firstName }} {{ row.item.lastName }}</strong>
							</template>
							<template #cell(email)="row">
								<a :href="`mailto:${row.item.email}`">{{ row.item.email }}</a>
							</template>
							<template #cell(businessName)="row">
								<span v-if="row.item.businessName">{{ row.item.businessName }}</span>
								<span v-else class="text-muted">—</span>
							</template>
							<template #cell(accountStatus)="row">
								<b-badge :variant="attendeeStatusVariant(row.item.accountStatus)">
									{{ attendeeStatusLabel(row.item.accountStatus) }}
								</b-badge>
							</template>
							<template #cell(actions)="row">
								<b-dropdown
									size="sm"
									variant="link"
									toggle-class="text-decoration-none"
									no-caret
									right
								>
									<template #button-content>
										<i class="fas fa-ellipsis-v text-muted"></i>
									</template>
									<b-dropdown-item @click="copyEmail(row.item.email)">
										Copy email
									</b-dropdown-item>
									<!-- Only show profile link for activated users — pending
									     tempUsers don't have a corresponding member profile yet. -->
									<b-dropdown-item
										v-if="row.item.accountStatus === 'user' && row.item.uid"
										@click="goToMember(row.item.uid)"
									>
										View member profile
									</b-dropdown-item>
								</b-dropdown>
							</template>
						</b-table>
					</Card>
				</div>
			</div>

			<!-- Payment + Error details -->
			<div class="row">
				<div class="col">
					<Card no-body>
						<b-row align-v="center" slot="header" class="px-2">
							<b-col><h3 class="mb-0">Payment</h3></b-col>
						</b-row>
						<div class="m-3">
							<b-row v-if="cardBrandLast4" align-v="center">
								<b-col cols="4"><small class="text-muted">Card</small></b-col>
								<b-col>
									<span class="text-capitalize">{{ cardBrandLast4.brand }}</span>
									<span class="text-muted">&middot;&middot;&middot;&middot; {{ cardBrandLast4.last4 }}</span>
								</b-col>
							</b-row>
							<b-row v-if="order.stripeChargeId" class="mt-2">
								<b-col cols="4"><small class="text-muted">Stripe Charge ID</small></b-col>
								<b-col><code class="small">{{ order.stripeChargeId }}</code></b-col>
							</b-row>
							<b-row v-if="order.stripeReceiptUrl" class="mt-2">
								<b-col cols="4"><small class="text-muted">Receipt</small></b-col>
								<b-col>
									<a :href="order.stripeReceiptUrl" target="_blank">
										View Stripe receipt
									</a>
								</b-col>
							</b-row>
							<b-row v-if="order.couponCode" class="mt-2">
								<b-col cols="4"><small class="text-muted">Coupon Applied</small></b-col>
								<b-col>{{ order.couponCode }}</b-col>
							</b-row>
							<b-row v-if="order.isTestMode" class="mt-2">
								<b-col cols="4"><small class="text-muted">Test Mode</small></b-col>
								<b-col><span class="text-warning">Yes</span></b-col>
							</b-row>
							<div v-if="order.errorMessage" class="mt-3 text-danger">
								<strong>Error:</strong> {{ order.errorMessage }}
							</div>
							<div
								v-if="order.provisioningErrors && order.provisioningErrors.length"
								class="mt-3"
							>
								<strong class="text-danger">Provisioning errors:</strong>
								<ul class="mb-0">
									<li
										v-for="(err, i) in order.provisioningErrors"
										:key="`prov-${i}`"
										class="text-danger small"
									>
										{{ err.email }} — {{ err.message }}
									</li>
								</ul>
							</div>
						</div>
					</Card>
				</div>
			</div>
		</div>

		<div v-else-if="!isLoading" class="container-fluid mt--6 text-center">
			<h5 class="text-muted py-5">Order not found.</h5>
		</div>
	</div>
</template>
<script>
import { collection, doc, getDoc, getDocs, query, where, limit } from '@firebase/firestore';
import { mapActions, mapGetters } from 'vuex';
import { getOrganizationFirestore } from '../../../../resources/organizationFirebase';
import { DashboardChildren } from '../../../../routes/routesNames';
import DashboardHeader from '../../HeaderFooterComponents/DashboardHeader.vue';
import Card from '../../../../components/Cards/Card.vue';

export default {
	name: 'RegistrationView',
	components: { DashboardHeader, Card },
	props: {
		orderId: { type: String, default: null },
	},
	computed: {
		...mapGetters({ organization: 'getOrganization' }),
		isExhibitor() {
			return !!(this.order && this.order.registrationType === 'exhibitor');
		},
		// Imports aren't visible to the template, so expose the route name here.
		vendorViewRouteName() {
			return DashboardChildren.vendorView.name;
		},
		people() {
			if (!this.order) return [];
			if (this.isExhibitor && Array.isArray(this.order.contacts)) {
				return this.order.contacts;
			}
			return Array.isArray(this.order.attendees) ? this.order.attendees : [];
		},
		purchaserName() {
			if (!this.order || !this.order.buyer) return 'Order';
			return `${this.order.buyer.firstName} ${this.order.buyer.lastName}`.trim();
		},
		// Card brand + last4 are populated by the backend after a successful Stripe
		// charge (see backend onlineConferenceCartPayment — captures from
		// payment_method_details.card). Null until that backend change ships.
		cardBrandLast4() {
			if (!this.order || !this.order.paymentMethod) return null;
			const pm = this.order.paymentMethod;
			if (!pm.brand || !pm.last4) return null;
			return { brand: pm.brand, last4: pm.last4 };
		},
		// Merge attendee account-status into the rows so the table can show whether
		// each attendee is still in tempUsers (pending signup) or migrated to users.
		attendeeRows() {
			return this.people.map((a) => {
				const lookup = this.attendeeAccountStatus[(a.email || '').toLowerCase()] || {};
				return Object.assign({}, a, {
					accountStatus: lookup.status || 'unknown',
					uid: lookup.uid || null,
				});
			});
		},
	},
	data() {
		return {
			isLoading: true,
			order: null,
			// keyed by lowercased email → { status: 'tempUser'|'user'|'unknown', uid }
			attendeeAccountStatus: {},
			buttonItems: [],
			itemFields: [
				{ key: 'productTitle', label: 'Item' },
				{ key: 'quantity', label: 'Qty' },
				{ key: 'unitAmount', label: 'Unit Price' },
				{ key: 'lineTotal', label: 'Line Total', class: 'text-right' },
			],
			attendeeFields: [
				{ key: 'name', label: 'Name' },
				{ key: 'email', label: 'Email' },
				{ key: 'businessName', label: 'Studio' },
				{ key: 'accountStatus', label: 'Account' },
				{ key: 'actions', label: '', class: 'text-right' },
			],
		};
	},
	methods: {
		...mapActions(['showNotification']),
		amountToDollars(cents) {
			if (cents == null) return '$0.00';
			return Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
			}).format(cents / 100);
		},
		formatTimestamp(value) {
			if (!value) return '';
			const date = value.toDate ? value.toDate() : new Date(value);
			return date.toLocaleString();
		},
		statusVariant(status) {
			switch (status) {
				case 'paid':
					return 'success';
				case 'pending':
				case 'partial':
					return 'warning';
				case 'failed':
					return 'danger';
				default:
					return 'secondary';
			}
		},
		attendeeStatusVariant(status) {
			switch (status) {
				case 'user':
					return 'success';
				case 'tempUser':
					return 'warning';
				default:
					return 'secondary';
			}
		},
		attendeeStatusLabel(status) {
			switch (status) {
				case 'user':
					return 'Activated';
				case 'tempUser':
					return 'Pending';
				default:
					return 'Unknown';
			}
		},
		// Kept for the DashboardHeader contract even though we currently expose
		// no buttons. Future quick-actions (refund, resend, etc.) plug in here.
		handleHeaderClick() {},
		copyEmail(email) {
			if (!email) return;
			navigator.clipboard.writeText(email).then(
				() =>
					this.showNotification({ type: 'success', message: `Copied ${email}` }),
				() =>
					this.showNotification({ type: 'danger', message: 'Copy failed.' })
			);
		},
		goToMember(uid) {
			this.$router.push({
				name: DashboardChildren.memberProfile.name,
				query: { uid },
			});
		},
		async getOrder() {
			if (!this.orderId) {
				this.isLoading = false;
				return;
			}
			const firestore = getOrganizationFirestore(this.organization);
			try {
				const snap = await getDoc(doc(firestore, `orders/${this.orderId}`));
				if (snap.exists()) {
					this.order = snap.data();
					await this.loadAttendeeAccountStatuses();
				} else {
					this.order = null;
				}
			} catch (error) {
				this.showNotification({ type: 'danger', message: error.message });
			} finally {
				this.isLoading = false;
			}
		},
		// For each attendee email, check users (= activated) and tempUsers (= pending)
		// so the table can show whether the conference confirmation email has been
		// claimed yet. tempUser presence after a paid order means the attendee
		// hasn't completed signup.
		async loadAttendeeAccountStatuses() {
			if (!this.order || this.people.length === 0) return;
			const firestore = getOrganizationFirestore(this.organization);
			const statusMap = {};
			for (const a of this.people) {
				const email = (a.email || '').trim();
				if (!email) continue;
				const key = email.toLowerCase();
				try {
					const userSnap = await getDocs(
						query(
							collection(firestore, 'users'),
							where('email', '==', email),
							limit(1)
						)
					);
					if (!userSnap.empty) {
						statusMap[key] = { status: 'user', uid: userSnap.docs[0].id };
						continue;
					}
					const tempSnap = await getDocs(
						query(
							collection(firestore, 'tempUsers'),
							where('email', '==', email),
							limit(1)
						)
					);
					if (!tempSnap.empty) {
						statusMap[key] = { status: 'tempUser', uid: tempSnap.docs[0].id };
						continue;
					}
					statusMap[key] = { status: 'unknown', uid: null };
				} catch (e) {
					statusMap[key] = { status: 'unknown', uid: null };
				}
			}
			this.attendeeAccountStatus = statusMap;
		},
	},
	mounted() {
		this.getOrder();
	},
};
</script>

<style scoped>
.totals-block {
	font-size: 0.875rem;
	line-height: 1.6;
	/* Match Bootstrap b-table cell padding (.75rem) so Subtotal / Transaction
	   Fee / Total Charged align with the column labels above. */
	padding: 0.75rem;
}
.totals-line + .totals-line {
	margin-top: 0.125rem;
}
.totals-grand {
	border-color: #e9ecef !important;
}
.totals-grand-label {
	font-weight: 600;
	font-size: 0.95rem;
	color: #32325d;
}
.totals-grand-value {
	font-weight: 600;
	font-size: 1rem;
	color: #32325d;
}
.business-logo {
	width: 96px;
	height: 96px;
	object-fit: contain;
	border: 1px solid #e9ecef;
	border-radius: 6px;
	background: #fff;
}
</style>
